import React from 'react';
import Seo, { faqLd, serviceLd } from '../../components/Seo';
import Reveal, { prefersReducedMotion } from '../../components/Reveal';
import {
  ChatTranscript, CheckItem, CompareTable, Faq, Icon, IconCards, Intro, ProductHero, QuoteSection, ScreenTour, SectionTitle,
  Steps, TrustStrip,
} from '../../components/product/ProductKit';
import { AI_RECEPTIONIST as D } from '../../data/aiReceptionist';
import { useSiteContent, withProductContent } from '../../content/siteContent';

const HeroVideo = () => (
  <div className="overflow-hidden rounded-3xl bg-[#0a1f2e] shadow-[0_30px_80px_-20px_rgba(10,31,46,0.35)]">
    {prefersReducedMotion() ? (
      <img src="/media/site/ai-receptionist.webp" alt="" className="aspect-[4/3] w-full object-cover" />
    ) : (
      <video poster="/media/site/ai-receptionist.webp" autoPlay muted loop playsInline className="aspect-[4/3] w-full object-cover" aria-hidden="true">
        <source src="/media/site/ai-receptionist.webm" type="video/webm" />
        <source src="/media/site/ai-receptionist.mp4" type="video/mp4" />
      </video>
    )}
  </div>
);

const AIReceptionist = () => {
  const P = withProductContent(useSiteContent('ai-receptionist'), D);
  return (
  <div className="min-h-screen bg-white text-[#0a1f2e]">
    <Seo
        title="AI Receptionist: 24/7 AI Call Answering"
        description="The VOIP2 AI Receptionist answers every call 24/7, understands what callers need, books appointments and transfers to the right person."
        path="/products/ai-receptionist"
        breadcrumbs={[{ name: 'AI Receptionist', path: '/products/ai-receptionist' }]}
        jsonLd={[serviceLd({ name: 'AI Receptionist', description: "The VOIP2 AI Receptionist answers every call 24/7, understands what callers need, books appointments and transfers to the right person.", path: '/products/ai-receptionist' }), faqLd(D.faqs)]}
      />

    <ProductHero {...P.hero} primary={{ label: 'Book a demo', href: '#quote' }} secondary={{ label: 'See a call', href: '#call' }} media={<HeroVideo />} />

    <TrustStrip items={P.trust} />

    <Intro {...P.intro} />

    <section id="platform" className="scroll-mt-20 bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionTitle title={D.platform.title} sub={D.platform.sub} />
        <ScreenTour screens={D.platform.screens} />
      </div>
    </section>

    <section className="px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionTitle title={D.features.title} sub={D.features.sub} />
        <IconCards items={D.features.items} />
      </div>
    </section>

    {/* Example call */}
    <section id="call" className="scroll-mt-20 bg-[#0a1f2e] px-4 py-24 text-white sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionTitle title={D.transcript.title} sub={D.transcript.sub} light />
          <Reveal as="ul" className="space-y-3 text-gray-200">
            {D.transcript.points.map((p) => <CheckItem key={p}>{p}</CheckItem>)}
          </Reveal>
        </div>
        <div className="rounded-3xl bg-[#f5f7f9] p-5 text-[#0a1f2e] md:p-8 lg:col-span-7">
          <div className="mb-5 flex items-center justify-between border-b border-gray-200 pb-4">
            <span className="flex items-center gap-3 font-semibold">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#c8ff00]"><Icon name="Phone" className="h-5 w-5" /></span>
              Incoming call · Northgate Dental
            </span>
            <span className="rounded-full bg-[#0a1f2e] px-3 py-1 text-xs font-semibold text-[#c8ff00]">Booking</span>
          </div>
          <ChatTranscript lines={D.transcript.lines} />
        </div>
      </div>
    </section>

    <section className="px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <SectionTitle title={D.compare.title} center />
        <CompareTable head={D.compare.head} rows={D.compare.rows} />
      </div>
    </section>

    <section className="bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionTitle title={D.industries.title} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {D.industries.items.map((c, i) => (
            <Reveal key={c.title} delay={(i % 3) * 70} className="flex flex-col rounded-2xl border border-gray-200 bg-white p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0a1f2e] text-[#c8ff00]"><Icon name={c.icon} className="h-5 w-5" /></span>
              <h3 className="mt-4 font-display text-lg font-semibold">{c.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-gray-600">{c.body}</p>
              <ul className="mt-4 flex flex-wrap gap-2">
                {c.points.map((p) => <li key={p} className="rounded-full bg-[#f5f7f9] px-3 py-1 text-xs font-semibold text-gray-700">{p}</li>)}
              </ul>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionTitle title="From demo to live calls" />
        <Steps steps={D.steps} />
      </div>
    </section>

    <Faq faqs={D.faqs} />

    <QuoteSection
      title="Hear it answer your calls"
      sub="Tell us how your calls are handled today and we will set up a demo for your business."
      service="AI Receptionist"
      contextLabel="Monthly call volume?"
      contextOptions={D.volumeOptions}
      related={D.related}
    />
  </div>
  );
};

export default AIReceptionist;
